/**
 * ProviderDetailsMedia Component
 * T129, T134-T135: Media gallery display with grid layout and sorting
 * 
 * Displays provider's photos and videos in a responsive grid gallery.
 * Matches the prototype design from ProviderProfile.tsx (lines 638-653).
 * 
 * Features:
 * - Responsive grid layout (2 cols mobile, 4 cols desktop) (T134)
 * - Media sorted by isPrimary DESC, displayOrder ASC (T135)
 * - Empty state for providers with no media
 * - Aspect ratio containers for consistent sizing
 * - Lazy loading for performance
 * 
 * Usage:
 * ```tsx
 * <ProviderDetailsMedia provider={provider} />
 * ```
 */

import { Card } from '@/shared/components/ui/card';
import { ImageIcon } from 'lucide-react';
import { ProviderMediaItem } from './ProviderMediaItem';
import type { ProviderProfile, ProviderMedia, ProviderPartnerCompany } from '../types/provider-profile.types';
import { getProviderDisplayName } from '../utils/formatters';

type ProviderUser = {
  firstName: string;
  lastName?: string | null;
};

type ProviderDetailsMediaProps = {
  provider: Pick<ProviderProfile, 'media' | 'companyName' | 'partnerCompany' | 'user'> | { 
    media: ProviderMedia[]; 
    companyName?: string;
    partnerCompany?: ProviderPartnerCompany | null;
    user?: ProviderUser | null;
  };
};

/**
 * Sort media by isPrimary DESC (primary first), then displayOrder ASC
 * T135: Implementation of sorting logic
 */
const sortMedia = (media: ProviderProfile['media']) => {
  return [...media].sort((a, b) => {
    // Primary media first
    if (a.isPrimary && !b.isPrimary) return -1;
    if (!a.isPrimary && b.isPrimary) return 1;
    
    // Then sort by displayOrder
    return a.displayOrder - b.displayOrder;
  });
};

/**
 * Displays provider's photo and video gallery
 * 
 * @param provider - Provider profile with media array
 */
export const ProviderDetailsMedia = ({ provider }: ProviderDetailsMediaProps) => {
  const { media } = provider;
  
  // FR-007e: Display name fallback chain - partnerCompany.name → companyName → (firstName + lastName)
  const partnerCompany = 'partnerCompany' in provider ? provider.partnerCompany : null;
  const companyName = 'companyName' in provider ? provider.companyName : null;
  const user = 'user' in provider ? provider.user : null;
  const displayName = getProviderDisplayName(partnerCompany, companyName, user);
  
  // T135: Sort media by isPrimary DESC, displayOrder ASC
  const sortedMedia = sortMedia(media);

  return (
    <Card className="p-6">
      {/* Section Header */}
      <h2 className="text-xl text-gray-900 mb-6 font-semibold">Photos & Videos</h2>

      {/* T134: Media Grid Layout */}
      {sortedMedia.length > 0 ? (
        <div
          data-testid="provider-media-gallery"
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {sortedMedia.map((mediaItem) => (
            <ProviderMediaItem key={mediaItem.id} media={mediaItem} />
          ))}
        </div>
      ) : (
        /* Empty State */
        <div
          data-testid="provider-media-gallery"
          className="text-center py-12"
        >
          <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Photos or Videos Available
          </h3>
          <p className="text-sm text-gray-600">
            {displayName} hasn't uploaded any media yet
          </p>
        </div>
      )}
    </Card>
  );
};

